import type { CargoItemRes, CargoRes } from 'api/swagger';
import { checkIsLastSelectedCargoItem } from 'shared/stores/helpers/select-cargo-helper.ts';
import { createStore } from './create-store';

type TState = {
  selectedCargos: Set<number>;
  selectedCargoItems: Set<number>;
  cargosWithSelection: Map<number, Set<number>>;
};

type TActions = {
  setSelectedCargo: (cargo: CargoRes) => void;
  deleteSelectedCargo: (cargo: CargoRes) => void;
  setSelectedCargoItem: (cargoItem: CargoItemRes, cargo: CargoRes) => void;
  deleteSelectedCargoItem: (cargoItem: CargoItemRes) => void;
  deleteAll: () => void;
};

export const useSelectCargoStore = createStore<TState & TActions>(
  (set) => ({
    selectedCargos: new Set(),
    selectedCargoItems: new Set(),
    cargosWithSelection: new Map(),

    setSelectedCargo: (cargo: CargoRes) => {
      set((state) => {
        const newCargoItems = state.selectedCargoItems;
        const newCargosWithSelection = new Map(state.cargosWithSelection);

        cargo.cargoItems.forEach((cargoItemId) => {
          newCargoItems.add(cargoItemId);
        });

        newCargosWithSelection.set(cargo.id, new Set(cargo.cargoItems));

        return {
          ...state,
          selectedCargos: state.selectedCargos.add(cargo.id),
          selectedCargoItems: newCargoItems,
          cargosWithSelection: newCargosWithSelection,
        };
      });
    },

    deleteSelectedCargo: (cargo: CargoRes) => {
      set((state) => {
        const newSelectedCargos = state.selectedCargos;
        const newSelectedCargoItems = state.selectedCargoItems;
        const newCargosWithSelection = new Map(state.cargosWithSelection);

        newSelectedCargos.delete(cargo.id);
        cargo.cargoItems.forEach((cargoItemId) => {
          newSelectedCargoItems.delete(cargoItemId);
        });

        newCargosWithSelection.delete(cargo.id);

        return {
          ...state,
          selectedCargos: newSelectedCargos,
          selectedCargoItems: newSelectedCargoItems,
          cargosWithSelection: newCargosWithSelection,
        };
      });
    },

    setSelectedCargoItem: (cargoItem: CargoItemRes, cargo: CargoRes) => {
      set((state) => {
        const newSelectedCargoItems = state.selectedCargoItems;
        const newSelectedCargos = state.selectedCargos;
        const newCargosWithSelection = new Map(state.cargosWithSelection);

        newSelectedCargoItems.add(cargoItem.id);
        if (checkIsLastSelectedCargoItem(cargo.cargoItems, newSelectedCargoItems, cargoItem.id)) {
          newSelectedCargos.add(cargo.id);
        }

        const existingSet = newCargosWithSelection.get(cargo.id) ?? new Set();
        existingSet.add(cargoItem.id);
        newCargosWithSelection.set(cargo.id, existingSet);

        return {
          ...state,
          selectedCargoItems: newSelectedCargoItems,
          selectedCargos: newSelectedCargos,
          cargosWithSelection: newCargosWithSelection,
        };
      });
    },
    deleteSelectedCargoItem: (cargoItem: CargoItemRes) => {
      set((state) => {
        const newSelectedCargos = state.selectedCargos;
        const newSelectedCargoItems = state.selectedCargoItems;
        const newCargosWithSelection = new Map(state.cargosWithSelection);

        newSelectedCargoItems.delete(cargoItem.id);
        newSelectedCargos.delete(cargoItem.cargoId);

        const existingSet = newCargosWithSelection.get(cargoItem.cargoId);
        if (existingSet) {
          existingSet.delete(cargoItem.id);
          if (existingSet.size === 0) {
            newCargosWithSelection.delete(cargoItem.cargoId);
          }
        }

        return {
          ...state,
          selectedCargoItems: newSelectedCargoItems,
          selectedCargos: newSelectedCargos,
          cargosWithSelection: newCargosWithSelection,
        };
      });
    },
    deleteAll: () => {
      set((state) => ({
        ...state,
        selectedCargos: new Set(),
        selectedCargoItems: new Set(),
        cargosWithSelection: new Map(),
      }));
    },
  }),
  'select-cargos-store',
);
